import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';

export type QuotaAdminList<T> = {
  page: number;
  page_size: number;
  total: number;
  list: T[];
};

export type QuotaGlobalControl = {
  enabled: boolean;
  revision: number;
  changed_by: string;
  change_reason: string;
  updated_at: string;
};

export type QuotaUsageHealth = {
  as_of_utc: string;
  attempts_24h: number;
  attempt_outcomes_24h: Record<string, number>;
  rejection_reasons_24h: Record<string, number>;
  client_versions_24h: Record<string, number>;
  complete_profiles: number;
  unknown_profiles: number;
  required_segments_48h: number;
  missing_segments_48h: number;
  invalid_segments_48h: number;
  dirty_business_dates: number;
  oldest_dirty_at_utc?: string;
  provisional_older_than_48h: number;
  last_compaction_started_utc?: string;
  last_compaction_completed_utc?: string;
  last_compacted_before_utc?: string;
  last_compaction_deleted_hours: number;
  last_compaction_skipped_hours: number;
  last_compaction_error?: string;
};

export type QuotaPacketHealth = {
  pending_slots: number;
  processing_slots: number;
  bound_packets: number;
  delivered_packets: number;
  activated_packets: number;
  credit_retry_packets: number;
  credit_failed_packets: number;
  credit_outbox_pending: number;
  credit_outbox_processing: number;
  credit_outbox_retry: number;
  credit_outbox_dead_letter: number;
  reconciliation_mismatch: number;
  reconciliation_unknown: number;
  oldest_pending_outbox_at_utc?: string;
};

export type QuotaUsageProfile = {
  user_id: string;
  terminal_count: number;
  task_elapsed_ms: number;
  active_duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  last_hour_start_utc: string;
  required_segments_48h: number;
  missing_segments_48h: number;
  invalid_segments_48h: number;
  completeness_state: string;
  channels: Record<string, QuotaUsageChannelMetric>;
};

export type QuotaUsageChannelMetric = {
  task_elapsed_ms: number;
  active_duration_ms: number;
  request_duration_ms: number;
  input_tokens: number;
  fresh_input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  cache_read_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  measured_input_tokens: number;
  estimated_input_tokens: number;
  measured_output_tokens: number;
  estimated_output_tokens: number;
  request_count: number;
  success_count: number;
};

export type QuotaUsageDaily = {
  user_id: string;
  terminal_id?: number;
  business_date: string;
  device_count?: number;
  task_elapsed_ms: number;
  active_duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  complete_hours: number;
  partial_hours: number;
  unsupported_hours: number;
  provisional_hours: number;
  channels: Record<string, QuotaUsageChannelMetric>;
};

export type QuotaUsageChannelStatsItem = {
  channel: string;
  user_count: number;
  user_share: number;
  task_elapsed_ms: number;
  task_share: number;
  active_duration_ms: number;
  active_share: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  token_share: number;
  request_count: number;
  success_count: number;
};

export type QuotaUsageChannelStats = {
  business_timezone: string;
  date_from: string;
  date_to: string;
  user_count: number;
  task_elapsed_ms: number;
  active_duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  request_count: number;
  channels: QuotaUsageChannelStatsItem[];
};

export type QuotaStrategyTemplate = {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  can_delete: boolean;
  reference_count: number;
};

export type QuotaStrategyRule = {
  metric: string;
  operator: '<' | '<=' | '=' | '>=' | '>';
  value: number;
  missing_policy: 'EXCLUDE' | 'UNKNOWN';
};

export type QuotaStrategyVersion = {
  id: number;
  template_id: number;
  version: number;
  business_timezone: 'Asia/Shanghai';
  round_times: string[];
  min_random_delay_seconds: number;
  max_random_delay_seconds: number;
  total_amount_minor: number;
  packet_count: number;
  min_amount_minor: number;
  max_amount_minor: number;
  delivery_ttl_seconds: number;
  activation_ttl_seconds: number;
  credited_quota_ttl_seconds: number;
  max_packets_per_day: number;
  max_packets_per_168_hours: number;
  require_first_balance_lte_zero: boolean;
  data_freshness_seconds: number;
  device_heartbeat_seconds: number;
  dispatch_window_seconds: number;
  delivery_ack_ttl_seconds: number;
  include_estimated_tokens: boolean;
  selection_algorithm: string;
  rule: { combinator: 'ALL' | 'ANY'; rules: QuotaStrategyRule[] };
  created_by: string;
  created_at: string;
};

export type QuotaStrategyTemplateDetail = QuotaStrategyTemplate & {
  versions: QuotaStrategyVersion[];
};

export type QuotaCampaign = {
  id?: number;
  business_date: string;
  strategy_version_id: number;
  strategy_version?: QuotaStrategyVersion;
  template_id?: number;
  template_name?: string;
  template_description?: string;
  status: string;
  total_amount_minor: number;
  packet_count: number;
  published_at?: string;
  rounds: Array<{
    round_index: number;
    base_dispatch_at: string;
    dispatch_deadline_at: string;
    status: string;
  }>;
  slots: Array<Record<string, unknown>>;
};

const useQuotaAdmin = <T>(path: string | null) => {
  const { data, error, mutate } = useSWR<T, Error>(
    path ? [path] : null,
    request.instance.get,
  );
  return { data, error, mutate, isLoading: Boolean(path && !data && !error) };
};

export const useQuotaGlobalControl = () =>
  useQuotaAdmin<QuotaGlobalControl>(
    '/answer/admin/api/quota-red-packet/global-control',
  );

export const updateQuotaGlobalControl = (
  enabled: boolean,
  expectedRevision: number,
  reason: string,
) =>
  request.put<QuotaGlobalControl>(
    '/answer/admin/api/quota-red-packet/global-control',
    {
      enabled,
      expected_revision: expectedRevision,
      reason,
    },
  );

export const useQuotaUsageHealth = () =>
  useQuotaAdmin<QuotaUsageHealth>(
    '/answer/admin/api/codex-usage/report-health',
  );

export const useQuotaPacketHealth = () =>
  useQuotaAdmin<QuotaPacketHealth>('/answer/admin/api/quota-red-packet/health');

export const useQuotaUsageProfiles = (params: {
  page: number;
  page_size: number;
  user_id?: string;
}) =>
  useQuotaAdmin<QuotaAdminList<QuotaUsageProfile>>(
    `/answer/admin/api/codex-usage/profiles?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );

export const useQuotaUsageDaily = (
  scope: 'daily-accounts' | 'daily-devices',
  params: {
    page: number;
    page_size: number;
    business_timezone: 'Asia/Shanghai';
    date_from: string;
    date_to: string;
    user_id?: string;
    terminal_id?: number;
  },
) =>
  useQuotaAdmin<QuotaAdminList<QuotaUsageDaily>>(
    `/answer/admin/api/codex-usage/${scope}?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );

export const useQuotaUsageChannelStats = (params: {
  business_timezone: 'Asia/Shanghai';
  date_from: string;
  date_to: string;
  user_id?: string;
}) =>
  useQuotaAdmin<QuotaUsageChannelStats>(
    `/answer/admin/api/codex-usage/channel-statistics?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );

export const useQuotaPackets = (params: {
  page: number;
  page_size: number;
  user_id?: string;
  status?: string;
}) =>
  useQuotaAdmin<QuotaAdminList<Record<string, unknown>>>(
    `/answer/admin/api/quota-red-packet/packets?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );

export const useQuotaCandidates = (params: {
  page: number;
  page_size: number;
  user_id?: string;
  result?: string;
  reason?: string;
}) =>
  useQuotaAdmin<QuotaAdminList<Record<string, unknown>>>(
    `/answer/admin/api/quota-red-packet/candidates?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );

export const useQuotaStrategyTemplates = () =>
  useQuotaAdmin<{ list: QuotaStrategyTemplate[] }>(
    '/answer/admin/api/quota-red-packet/templates',
  );

export const useQuotaStrategyTemplate = (templateId?: number) =>
  useQuotaAdmin<QuotaStrategyTemplateDetail>(
    templateId
      ? `/answer/admin/api/quota-red-packet/templates/${templateId}`
      : null,
  );

export const useQuotaCampaigns = (dateFrom: string, dateTo: string) =>
  useQuotaAdmin<{ list: QuotaCampaign[] }>(
    `/answer/admin/api/quota-red-packet/campaigns?${qs.stringify({
      date_from: dateFrom,
      date_to: dateTo,
    })}`,
  );

export const createQuotaStrategyTemplate = (
  name: string,
  description: string,
) =>
  request.post<QuotaStrategyTemplate>(
    '/answer/admin/api/quota-red-packet/templates',
    { name, description },
  );

export const deleteQuotaStrategyTemplate = (templateId: number) =>
  request.delete(`/answer/admin/api/quota-red-packet/templates/${templateId}`);

export const createQuotaStrategyVersion = (
  templateId: number,
  payload: Omit<
    QuotaStrategyVersion,
    | 'id'
    | 'template_id'
    | 'version'
    | 'selection_algorithm'
    | 'created_by'
    | 'created_at'
  >,
) =>
  request.post<QuotaStrategyVersion>(
    `/answer/admin/api/quota-red-packet/templates/${templateId}/versions`,
    payload,
  );

export const previewQuotaCampaign = (
  strategyVersionId: number,
  businessDate: string,
) =>
  request.post<QuotaCampaign>(
    '/answer/admin/api/quota-red-packet/campaigns/preview',
    {
      strategy_version_id: strategyVersionId,
      business_date: businessDate,
    },
  );

export const publishQuotaCampaign = (
  strategyVersionId: number,
  businessDate: string,
) =>
  request.post<QuotaCampaign>(
    '/answer/admin/api/quota-red-packet/campaigns/publish',
    {
      strategy_version_id: strategyVersionId,
      business_date: businessDate,
    },
  );

export const changeQuotaCampaignStatus = (
  campaignId: number,
  action: 'pause' | 'resume' | 'cancel',
) =>
  request.post<QuotaCampaign>(
    `/answer/admin/api/quota-red-packet/campaigns/${campaignId}/${action}`,
  );

export const getQuotaCampaign = (campaignId: number) =>
  request.get<QuotaCampaign>(
    `/answer/admin/api/quota-red-packet/campaigns/${campaignId}`,
  );

export const retryQuotaCreditOutbox = (outboxId: number, reason: string) =>
  request.post(`/answer/admin/api/quota-red-packet/outbox/${outboxId}/retry`, {
    reason,
  });
